/**
 * The single place that decides which config layer a level-filter edit lands
 * in, composes the four-layer stack (`context → view → user → session`) per
 * level via `composeLevelFilters`, and produces the `RuntimeFilterEnvelope`
 * `useContextTree`/`contextTreeApi` send.
 *
 * See `specs/per-level-context-filters.md` § UI Components ("Hook") and
 * Phase 4.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from '@center-technologies/analytics';
import { concatUnifiedFilters, hasAnyFilterLane } from '@center-platform/spheres/filters';
import type { ApiClients } from '../context/ApiClientsContext';
import type { ContextDefinition, RuntimeFilterEnvelope } from 'types/context';
import type { UnifiedFilters } from 'types/filters';
import type {
  BaseViewConfig,
  BaseViewConfigPatch,
  ComposedLevelFilters,
  DegradedFilterEntry,
  FilterLayer,
  FilterProvenanceEntry,
} from 'types/contextView';
import {
  composeLevelFilters,
  groupComposedFiltersByLayer,
  narrowLevelFiltersToAspect,
  type LevelFilterTierRow,
} from '../utils/composeLevelFilters';
import { deepMergeJson } from '../utils/deepMergeJson';
import { useViewState } from '../context/ViewStateContext';
import { useViewPersonalization, type UseViewPersonalizationReturn } from './useViewPersonalization';
import { getContextView, updateContextView, type ContextViewRecord } from '../api/contextApi';
import {
  buildFilterPropertyOptions,
  resolveLevelEligibleSchemas,
  type SchemaForFilters,
  type LevelForScoping,
} from '../utils/filterPropertyBuilder';

/** Where a committed level-filter edit is persisted. Blast radius grows left to right. */
export type FilterSaveTarget = 'session' | 'user' | 'view';

/**
 * The pseudo-level id `setLevelFilter`/`byLevel` use for the view-wide scope
 * (Atlas column filters — `BaseViewConfig.globalFilters`). Not a real
 * `ContextLevel.id`; `setLevelFilter('*', …)` patches `globalFilters` instead
 * of `levelFilters[levelId]`, and `byLevel['*']` composes that layer instead
 * of one level's `level.filters`.
 */
export const VIEW_WIDE_SCOPE = '*';

/**
 * Why a save target is unavailable, or why choosing it does more than save —
 * `FilterSaveTargetMolecule` maps this to translated copy. `already_applied`
 * is not produced by this hook: it is the persistent bar's reason for
 * `session`/`user`, whose clauses are written at the moment of authoring and
 * so have nothing left for a later Save to commit (EAINT-789 — only the
 * deferred `view` buffer is commitable from the bar).
 *
 * `link_exits_first` is the one reason that accompanies an ENABLED row: while
 * a shared link suppresses the recipient's own layers, choosing `session` or
 * `user` leaves shared-link mode and then saves (EAINT-793 § Edge Cases, R-1).
 * The row must still say so, because the action has a consequence beyond the
 * save — see `saveTargets`.
 */
export type FilterSaveTargetReason =
  | 'no_active_view'
  | 'no_view_edit_permission'
  | 'permission_pending'
  | 'already_applied'
  | 'link_active'
  | 'link_exits_first';

export interface FilterSaveTargetAvailability {
  target: FilterSaveTarget;
  disabled: boolean;
  /**
   * Present on a disabled row to explain the block, and on an enabled row
   * only for `link_exits_first`, where the reason describes a side effect
   * rather than a refusal. Renderers must therefore show `reason` whenever it
   * is set, not only when `disabled`.
   */
  reason?: FilterSaveTargetReason;
}

/** Stable empty default — a fresh `[]` on every render would invalidate the `degraded` memo for no reason. */
const EMPTY_SCHEMAS: SchemaForFilters[] = [];

/** `buildFilterPropertyOptions` only needs `key`s for degraded detection, so a passthrough is a safe default when no real `t` is supplied. */
const DEFAULT_TRANSLATE = (key: string, options?: { defaultValue: string; [param: string]: unknown }): string =>
  options?.defaultValue ?? key;

/**
 * Lanes degraded detection walks (EAINT-789 § Degraded-filter detection).
 * `arcFilters` and `filterGroups` are deliberately excluded — `filterGroups`
 * nest arbitrarily (walking them is deferred) and `arc` is a correlated
 * pseudo-filter with no single "target" the property catalog can vouch for.
 */
const DEGRADABLE_LANES = new Set<FilterProvenanceEntry['lane']>([
  'propertyFilters',
  'perspectiveFilters',
  'relationshipFilters',
]);

/**
 * The `buildFilterPropertyOptions` key a provenance entry is checked against.
 *
 * `perspectiveFilters`/`relationshipFilters` clauses name a specific
 * perspective/relationship, but that catalog only carries the generic
 * 'perspective'/'relationship' pseudo-property's presence, not per-name
 * validity — so those lanes check the pseudo-property, not the clause's own
 * target string. Reuses the picker's one source rather than inventing a second
 * notion of "available" (EAINT-789 § Degraded-filter detection).
 */
const catalogKeyForLane = (entry: FilterProvenanceEntry): string => {
  if (entry.lane === 'propertyFilters') return entry.target as string;
  return entry.lane === 'perspectiveFilters' ? 'perspective' : 'relationship';
};

export interface UseLevelFiltersParams {
  apiClients: ApiClients | null;
  contextSystemName: string | null | undefined;
  contextDefinition: ContextDefinition | null | undefined;
  viewSystemName: string | null | undefined;
  /** The shared view record — its `base_config.levelFilters` is the 'view' layer. `null`/`undefined` when no `ContextView` is active (implicit default view). */
  view: ContextViewRecord | null | undefined;
  /** Whether the caller may write the shared view's filters (`context_view.edit`), from `useContextViewPermissions`. Fail-closed by construction — the decision and the key it was resolved for are ONE value in that hook, so any key it does not match yields all-false. (It deliberately does NOT blank the value before a fetch; blanking while leaving the key in place is what made a matching key stop implying "resolved for this key".) */
  canEditView: boolean;
  /**
   * Whether `canEditView` is still resolving (`useContextViewPermissions(...).loading`).
   * Distinguishes "we know you may not" (`no_view_edit_permission`) from
   * "we don't know yet" (`permission_pending`) — see D-3. The switch-guard
   * still fires on a pending buffer during load regardless of this flag: the
   * rail fails OPEN on an unknown permission, since suppressing it risks
   * silently discarding work.
   */
  canEditViewLoading?: boolean;
  /** Fired with the updated record after a successful shared-view write, so the caller's own `view` state stays in sync. */
  onViewUpdated?: (view: ContextViewRecord) => void;
  onWriteError?: (error: unknown) => void;
  /**
   * Fired when a shared-filter commit is refused with a 403 — the caller
   * (`TreeMapOrganism`) invalidates its view-permission cache so the UI stops
   * asserting an edit right the server just denied (EAINT-789 Edge Cases).
   */
  onViewPermissionDenied?: () => void;
  /**
   * The org-wide schema catalog — `degraded` detection's property source
   * (`buildFilterPropertyOptions`) and `resolveLevelEligibleSchemas`'s
   * hierarchy fallback. Defaults to `[]`, under which `degraded` is empty —
   * an absent catalog cannot vouch for a clause, but neither can it condemn
   * one. The caller that renders `degraded` (EAINT-789 Phase 3/4) is
   * responsible for supplying real schema data and `schemasLoading`.
   */
  schemas?: SchemaForFilters[];
  /**
   * Whether `schemas` is still being fetched (`useAvailableTypes(...).loading`).
   * While true, `degraded` is EMPTY regardless of what the catalog says: an
   * unresolved catalog is indistinguishable from an empty one, and treating it
   * as authoritative marks every clause on every level "no longer available" —
   * complete with a live remove control, which for the `view` layer deletes the
   * clause for the whole organization. "We don't know yet" must not render as
   * "we know it's gone" (EAINT-789 gap analysis G-9).
   */
  schemasLoading?: boolean;
  /** The context's own applicability filter — `resolveLevelEligibleSchemas`'s root-level (first-level) fallback. */
  contextApplicability?: UnifiedFilters;
  /** Translation function for `buildFilterPropertyOptions`'s catalog. Defaults to a passthrough — `degraded` only reads catalog `key`s, never labels. */
  t?: (key: string, options?: { defaultValue: string; [param: string]: unknown }) => string;
  /**
   * The decoded envelope for an active `?f=` shared filter link (EAINT-793),
   * from `useSharedFilterLink`. Presence — not content — activates link mode
   * for every level: passing this (even as `{}`) makes `byLevel[id].runtime`
   * exactly the link's contribution for that level and suppresses `view`/
   * `user`/`session` there, per `composeLevelFilters`'s `link` layer.
   * `undefined` (the default) reproduces pre-EAINT-793 composition exactly.
   */
  linkFilters?: RuntimeFilterEnvelope;
  /**
   * The aspect (property/perspective/relationship/arc-template name) the
   * view-wide scope's context tier narrows to — Atlas's column popover set
   * to the column's own aspect. `undefined` (the default) means no caller
   * needs the tier yet, so `globalContextTiers` stays empty; the `'*'` scope
   * itself (`byLevel['*']`, `runtimeFilters.global`) always composes
   * regardless, since `globalFilters` costs nothing to compute when absent.
   */
  viewWideAspect?: string;
}

export interface UseLevelFiltersReturn {
  /**
   * `ComposedLevelFilters` per `ContextLevel.id`, plus `VIEW_WIDE_SCOPE`
   * (`'*'`) composing `globalFilters` (view) / the personal overlay /
   * session / link the same way any level does.
   */
  byLevel: Record<string, ComposedLevelFilters>;
  /** The envelope for `useContextTree`/`contextTreeApi` — `undefined` when neither a level nor the view-wide scope carries a runtime clause. */
  runtimeFilters: RuntimeFilterEnvelope | undefined;
  /**
   * The view-wide scope's context tier, narrowed to `viewWideAspect` — one
   * row per level with a clause on that aspect. Empty when `viewWideAspect`
   * is not supplied.
   */
  globalContextTiers: LevelFilterTierRow[];
  /** Which save targets the caller may currently commit to, with a reason code for any that are disabled. Always all three, in blast-radius order — never hidden. */
  saveTargets: FilterSaveTargetAvailability[];
  /**
   * Commits a level's draft filters to the given layer. `'session'`/`'user'`
   * write through immediately, as before. `'view'` no longer writes — it
   * buffers into a pending shared-filter draft keyed to the current
   * `(contextSystemName, viewSystemName)` (EAINT-789 D-1); `commitPendingSharedFilters`
   * owns the actual write. No-ops (fail-closed) when there is no active `view`
   * to eventually target.
   */
  setLevelFilter: (levelId: string, filters: UnifiedFilters, target: FilterSaveTarget) => void;
  /** True when a shared-view filter edit is buffered and not yet persisted — the toolbar's switch-guard dirty signal (EAINT-789 SC1). */
  hasPendingSharedFilters: boolean;
  /**
   * `levelId`s with a currently-buffered, uncommitted shared-filter edit —
   * the per-level counterpart of `hasPendingSharedFilters`. A level in this
   * set has its ENTIRE `view`-layer clause set replaced by the pending draft
   * (`setLevelFilter` buffers the whole level, not a lane-by-lane merge), so
   * every one of that level's `view`-layer provenance entries is unsaved.
   * Lets a caller (`ContextFilterBarMolecule`) attribute "unsaved" to the
   * view layer only where a write is actually pending, rather than to every
   * `view`-layer clause regardless of commit state.
   */
  pendingSharedFilterLevelIds: string[];
  /**
   * Commits the pending shared-filter buffer, re-deriving save-target
   * availability at commit time rather than trusting the state the buffer
   * was created under (EAINT-789 D-1/Security Considerations). Resolves
   * `true` when the buffer is empty or was successfully written; resolves
   * `false` (a silent no-op, buffer retained) when the target became
   * unavailable between entry and commit; rejects if the write itself fails
   * (invoking `onViewPermissionDenied` first on a 403).
   *
   * `overrides` folds extra `levelId -> filters` entries into the buffer for
   * THIS commit only — the "Share with view…" confirm action's one-shot
   * write. `setLevelFilter(id, filters, 'view')` buffers via `setState`,
   * which is not visible in this closure until the next render, so a caller
   * that both buffers and immediately commits in the same handler would
   * commit the buffer as it stood before that `setState` — the previous
   * pending entry, or nothing at all. `overrides` lets the caller pass the
   * just-confirmed clauses directly into the same write instead.
   */
  commitPendingSharedFilters: (overrides?: Record<string, UnifiedFilters>) => Promise<boolean>;
  /** Discards the pending shared-filter buffer without writing it — the Discard side of the switch-guard dialog. */
  discardPendingSharedFilters: () => void;
  /** Clears session + personal clauses for every level. Never touches the view or context layers. */
  reset: () => void;
  /**
   * Composed clauses whose target no longer exists in the level's catalog —
   * still sent (the engines already fail them closed to match-nothing), so
   * a caller can render "no longer available" and offer removal (EAINT-789
   * § Degraded-filter detection). Checked only on `propertyFilters`/
   * `perspectiveFilters`/`relationshipFilters`; `arcFilters`/`filterGroups`
   * are deferred. Distinct from a read-gated `access.read` 403 (D-2): that
   * refusal is live and permission-bearing, not stale, and this array
   * cannot see it — `buildFilterPropertyOptions` has no notion of per-property
   * read access, so a read-gated property is still in the catalog.
   */
  degraded: DegradedFilterEntry[];
  /**
   * Whether the caller may remove an individual clause on the given layer —
   * the single predicate `ContextFilterBarMolecule`/`FilterSummaryGroupMolecule`
   * call instead of special-casing `layer === 'view'` themselves (EAINT-789
   * § Design System Reuse Audit). `context` is always `false` (not the
   * caller's to remove); `view` mirrors `saveTargets`'s `view` availability
   * (same `context_view.edit` gate, re-derived live rather than duplicated);
   * `user`/`session` are always `true` — every session/personal clause is
   * the current user's own. Orthogonal to `degraded`: a stale clause is
   * still gated by who owns the layer it lives on, not by its staleness.
   */
  canRemoveLayer: (layer: FilterLayer) => boolean;
  /**
   * Removes one clause from a level's `session`/`user`/`view` layer,
   * identified by the `FilterProvenanceEntry.filterId` `composeLevelFilters`
   * minted for it. Replaces that layer's clause set for the level via the
   * same `setLevelFilter` every other write on this hook uses — a `view`
   * removal buffers exactly like any other `view` edit (EAINT-789 D-1) and
   * still needs `commitPendingSharedFilters` to persist. No-ops for
   * `layer === 'context'` and for an unresolvable `(levelId, layer, lane,
   * filterId)` — defensive, since the UI never renders a remove control a
   * caller hasn't already gated with `canRemoveLayer`.
   */
  removeFilterClause: (
    levelId: string,
    layer: FilterLayer,
    lane: FilterProvenanceEntry['lane'],
    filterId: string
  ) => void;
  /** The underlying personalization hook — exposed so callers needing `hasOverlay`/full `reset()` (Card A) don't have to instantiate a second one. */
  personalization: UseViewPersonalizationReturn;
}

export function useLevelFilters(params: UseLevelFiltersParams): UseLevelFiltersReturn {
  const {
    apiClients,
    contextSystemName,
    contextDefinition,
    viewSystemName,
    view,
    canEditView,
    canEditViewLoading = false,
    onViewUpdated,
    onWriteError,
    onViewPermissionDenied,
    schemas = EMPTY_SCHEMAS,
    schemasLoading = false,
    contextApplicability,
    t: translate = DEFAULT_TRANSLATE,
    linkFilters,
    viewWideAspect,
  } = params;

  const logAnalyticsEvent = useAnalytics();

  const { session, patchBase: patchSessionBase } = useViewState(contextSystemName ?? '', viewSystemName);
  const personalization = useViewPersonalization(apiClients, contextSystemName, viewSystemName, {
    onWriteError,
  });

  const levels = useMemo(() => contextDefinition?.levels ?? [], [contextDefinition]);

  // ── Pending shared-filter buffer (EAINT-789 D-1) ───────────────────────────
  // A 'view'-target edit no longer writes immediately; it lands here until
  // `commitPendingSharedFilters` is called. Keyed implicitly to the
  // `(contextSystemName, viewSystemName)` this effect is scoped to — dropped
  // outright when either changes, so a draft authored against one view can
  // never commit against another.
  const [pendingSharedFilters, setPendingSharedFilters] = useState<Record<string, UnifiedFilters>>({});
  const pendingKeyRef = useRef<string>('');
  const currentKey = `${contextSystemName ?? ''}::${viewSystemName ?? ''}`;
  useEffect(() => {
    if (pendingKeyRef.current !== currentKey) {
      pendingKeyRef.current = currentKey;
      setPendingSharedFilters({});
    }
  }, [currentKey]);

  // Presence, not content, activates link mode — an active link with nothing
  // for a given level still suppresses that level's view/user/session (G-4);
  // only the ABSENCE of `linkFilters` reproduces pre-EAINT-793 composition.
  const linkActive = linkFilters !== undefined;

  const byLevel = useMemo(() => {
    const result: Record<string, ComposedLevelFilters> = {};
    levels.forEach((level) => {
      result[level.id] = composeLevelFilters({
        context: level.filters,
        view: pendingSharedFilters[level.id] ?? view?.base_config?.levelFilters?.[level.id],
        user: personalization.overlay.base.levelFilters?.[level.id],
        session: session.base.levelFilters?.[level.id],
        ...(linkActive
          ? { link: concatUnifiedFilters(linkFilters?.global, linkFilters?.byLevelId?.[level.id]) ?? {} }
          : {}),
      });
    });
    // The view-wide scope ('*') composes exactly like any level, except its
    // `context` contribution isn't one value — it's a per-level tier list
    // (`globalContextTiers`) — so `context` is omitted here.
    result[VIEW_WIDE_SCOPE] = composeLevelFilters({
      context: undefined,
      view: pendingSharedFilters[VIEW_WIDE_SCOPE] ?? view?.base_config?.globalFilters,
      user: personalization.overlay.base.globalFilters,
      session: session.base.globalFilters,
      ...(linkActive ? { link: linkFilters?.global ?? {} } : {}),
    });
    return result;
  }, [levels, view, personalization.overlay, session, pendingSharedFilters, linkActive, linkFilters]);

  const globalContextTiers = useMemo<LevelFilterTierRow[]>(() => {
    if (!viewWideAspect) return [];
    return narrowLevelFiltersToAspect(levels, viewWideAspect);
  }, [levels, viewWideAspect]);

  const runtimeFilters = useMemo<RuntimeFilterEnvelope | undefined>(() => {
    const byLevelId: Record<string, UnifiedFilters> = {};
    levels.forEach((level) => {
      const runtime = byLevel[level.id]?.runtime;
      if (runtime && hasAnyFilterLane(runtime)) {
        byLevelId[level.id] = runtime;
      }
    });
    const global = byLevel[VIEW_WIDE_SCOPE]?.runtime;
    const envelope: RuntimeFilterEnvelope = {};
    if (global && hasAnyFilterLane(global)) envelope.global = global;
    if (Object.keys(byLevelId).length > 0) envelope.byLevelId = byLevelId;
    return Object.keys(envelope).length > 0 ? envelope : undefined;
  }, [levels, byLevel]);

  // `ContextLevel.filters` is the persisted `UnifiedFilters` shape (optional
  // `propertyFilters`); `LevelForScoping.filters` types it as the
  // editor-local `FilterState` (required `propertyFilters`), which keeps the
  // two nominally distinct even though `resolveLevelEligibleSchemas` only
  // ever reads `.propertyFilters`/`.filterGroups` off it structurally.
  const levelsForScoping = useMemo<LevelForScoping[]>(() => levels as unknown as LevelForScoping[], [levels]);

  const degraded = useMemo<DegradedFilterEntry[]>(() => {
    const result: DegradedFilterEntry[] = [];

    // An unresolved catalog cannot vouch for anything, and an empty one is
    // indistinguishable from it — so both stay silent rather than declaring
    // every clause stale. See `schemasLoading`.
    if (schemasLoading || schemas.length === 0) return result;

    levelsForScoping.forEach((levelForScoping) => {
      const composed = byLevel[levelForScoping.id];
      if (!composed) return;

      const relevant = composed.provenance.filter(
        (entry) => DEGRADABLE_LANES.has(entry.lane) && entry.target !== undefined
      );
      if (relevant.length === 0) return;

      const eligibleSchemas = resolveLevelEligibleSchemas(
        levelForScoping,
        levelsForScoping,
        contextApplicability,
        schemas
      );
      const catalogKeys = new Set(
        buildFilterPropertyOptions({ schemas, eligibleSchemas, t: translate }).map((option) => option.key)
      );

      relevant.forEach((entry) => {
        if (!catalogKeys.has(catalogKeyForLane(entry))) {
          result.push({
            levelId: levelForScoping.id,
            filterId: entry.filterId,
            lane: entry.lane,
            missingTarget: entry.target as string,
            layer: entry.layer,
          });
        }
      });
    });

    return result;
  }, [levelsForScoping, byLevel, schemas, schemasLoading, contextApplicability, translate]);

  // Shared by `saveTargets` (render-time, for menu display) and
  // `commitPendingSharedFilters` (commit-time, re-derived rather than
  // trusted from render — EAINT-789 D-1). `permission_pending` is distinct
  // from `no_view_edit_permission`: the gate stays disabled either way, but
  // the explanation must not tell a permitted user they lack permission
  // just because the lookup hasn't landed yet (D-3).
  // `linkActive` outranks every other reason: while a shared link is active
  // the view layer contributes nothing to `runtime` (composeLevelFilters
  // replaces it), so a save would commit a clause that isn't even part of
  // what the recipient sees (permissions #13, specs/shareable-context-links.md).
  const deriveViewSaveReason = useCallback((): FilterSaveTargetReason | undefined => {
    if (linkActive) return 'link_active';
    if (!viewSystemName || !view) return 'no_active_view';
    if (canEditViewLoading) return 'permission_pending';
    if (!canEditView) return 'no_view_edit_permission';
    return undefined;
  }, [linkActive, viewSystemName, view, canEditViewLoading, canEditView]);

  // While a link is active, `session`/`user` must NOT read as plain
  // `disabled: false` (EAINT-793 R-1). They stay selectable — the ratified
  // edge case is that the recipient's edit lands and shared mode ends, not
  // that editing is refused — but the consumer has to be able to SEE that a
  // save here cannot be composed against the suppressed layers as they stand.
  // `LevelFilterPopoverOrganism` reads this reason to defer the write until
  // after the exit restores the real base; the menu renders it so the row
  // announces the side effect. Reporting a bare `disabled: false` is what let
  // the popover concat against `{}` and delete the recipient's own clauses.
  const saveTargets = useMemo<FilterSaveTargetAvailability[]>(() => {
    const viewReason = deriveViewSaveReason();
    const personalReason: FilterSaveTargetReason | undefined = linkActive ? 'link_exits_first' : undefined;
    return [
      { target: 'session', disabled: false, reason: personalReason },
      { target: 'user', disabled: false, reason: personalReason },
      { target: 'view', disabled: Boolean(viewReason), reason: viewReason },
    ];
  }, [deriveViewSaveReason, linkActive]);

  const setLevelFilter = useCallback(
    (levelId: string, filters: UnifiedFilters, target: FilterSaveTarget) => {
      const patch: Partial<BaseViewConfig> =
        levelId === VIEW_WIDE_SCOPE ? { globalFilters: filters } : { levelFilters: { [levelId]: filters } };

      if (target === 'session') {
        patchSessionBase(patch);
      } else if (target === 'user') {
        personalization.patchBase(patch);
      } else {
        // No availability gate here (EAINT-789 D-1) — a disabled row is a UX
        // affordance, never the enforcement, so entry-time state is never
        // trusted. Buffer unconditionally as long as there's a `view` to
        // eventually target; `commitPendingSharedFilters` re-derives
        // availability for real at write time.
        if (!view) return;
        setPendingSharedFilters((prev) => ({ ...prev, [levelId]: filters }));
      }

      logAnalyticsEvent('context_filter_applied', undefined, {
        contextSystemName: contextSystemName ?? null,
        levelId,
        layer: target,
        laneCounts: {
          property: filters.propertyFilters?.length ?? 0,
          perspective: filters.perspectiveFilters?.length ?? 0,
          relationship: filters.relationshipFilters?.length ?? 0,
          arc: filters.arcFilters?.length ?? 0,
          group: filters.filterGroups?.length ?? 0,
        },
      });
    },
    [patchSessionBase, personalization, view, contextSystemName, logAnalyticsEvent]
  );

  const hasPendingSharedFilters = Object.keys(pendingSharedFilters).length > 0;

  const commitPendingSharedFilters = useCallback(async (overrides: Record<string, UnifiedFilters> = {}): Promise<boolean> => {
    const sent = { ...pendingSharedFilters, ...overrides };
    if (Object.keys(sent).length === 0) return true;

    // Re-derived, not read from the memoized `saveTargets` — the buffer may
    // have been filled under a permission that has since changed (EAINT-789
    // Edge Cases: "cannot happen" was wrong four ways over).
    const reason = deriveViewSaveReason();
    const schemaClient = apiClients?.schemaClient;
    if (reason || !schemaClient || !view) {
      // Unavailable, not an error — the buffer is retained for a later retry
      // ("Save for me" once the bar exists, Phase 3).
      return false;
    }

    // Snapshot what is actually being sent (buffer plus this commit's own
    // overrides). Only these keys may be cleared on success — a
    // `setLevelFilter(…, 'view')` issued while the PUT is in flight is NOT in
    // this write, and clearing the buffer wholesale would discard it AND flip
    // `hasPendingSharedFilters` false, so the switch-guard would not protect
    // it either.
    const sentLevelIds = Object.keys(sent);
    const { [VIEW_WIDE_SCOPE]: sentGlobalFilters, ...sentLevelFilters } = sent;

    const patch: Partial<BaseViewConfig> = {
      ...(Object.keys(sentLevelFilters).length > 0 ? { levelFilters: sentLevelFilters } : {}),
      ...(sentGlobalFilters !== undefined ? { globalFilters: sentGlobalFilters } : {}),
    };

    try {
      // Read the record back immediately before the PUT, for the same reason
      // the view editor does (`ContextViewEditorOverlayOrganism`): this route
      // REPLACES `base_config` wholesale, and `view` here is whatever the
      // organism last fetched. Editing the active view in the overlay does not
      // change `viewSystemName`, so `useContextView`'s effect never refires and
      // the in-memory record can be arbitrarily stale — merging into it would
      // revert `visibleProperties`, `cardDensity`, `levelOverrides` and the
      // rest for the whole org. This hook authors `levelFilters` and nothing
      // else; every other key is passed through from storage. EAINT-789.
      const stored = await getContextView(schemaClient, view.system_name);
      const mergedBaseConfig = deepMergeJson(
        stored.base_config as unknown as Record<string, unknown>,
        patch as Record<string, unknown>
      ) as BaseViewConfig;

      const updated = await updateContextView(schemaClient, view.system_name, { baseConfig: mergedBaseConfig });
      setPendingSharedFilters((prev) => {
        const remaining = { ...prev };
        sentLevelIds.forEach((levelId) => {
          // Only if untouched since the snapshot — an edit made during the
          // write replaced the reference and is still uncommitted.
          if (remaining[levelId] === sent[levelId]) delete remaining[levelId];
        });
        return remaining;
      });
      onViewUpdated?.(updated);
      return true;
    } catch (err: unknown) {
      const status =
        (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        // The buffer is retained by construction — nothing above cleared it.
        onViewPermissionDenied?.();
      }
      onWriteError?.(err);
      throw err;
    }
  }, [pendingSharedFilters, deriveViewSaveReason, apiClients, view, onViewUpdated, onWriteError, onViewPermissionDenied]);

  const discardPendingSharedFilters = useCallback(() => {
    setPendingSharedFilters({});
  }, []);

  // Null-delete (EAINT-783/G-10), not an empty-`UnifiedFilters` tombstone —
  // `deepMergeJson`/`jsonb_deep_merge` remove the level's `levelFilters` key
  // rather than storing an explicit "no clauses" value there.
  //
  // This holds even when the stored config has NO `levelFilters` key at all,
  // which is every row that has never had a level filter saved — i.e. the case
  // reset() actually hits most often. It did not always: both implementations
  // used to assign the patch object verbatim when the target at that key was not
  // already an object, so those nulls were STORED rather than deleting, and
  // accumulated unbounded (`levelFilters` is unnamed under `baseConfig`'s
  // `additionalProperties: true`, so its contents escape the 200-key cap that
  // applies to the NAMED `levelOverrides`/`columnState`). Fixed in EAINT-801 by
  // recursing into `{}` per RFC 7386 §2 — see migration 20260817120000 and
  // `deletesANestedNullWhenTHEPARENTKeyIsAbsent`.
  const reset = useCallback(() => {
    if (levels.length === 0) return;
    /**
     * The third destructive reset under a link (EAINT-793 R-1's class). This writes
     * `levelFilters: null` for EVERY level to both the session layer and the autosaved
     * personal overlay — i.e. it permanently deletes clauses that a link is currently
     * suppressing, so the user cannot see what they are deleting.
     *
     * Its two sibling paths (`personalization.reset()`, `resetSession()`) were given
     * exit-first deferrals; this one was protected only INCIDENTALLY, by the bar's
     * `disabled={resettableCount === 0}` — and `resettableCount` counts `session`/`user`
     * provenance, which `composeLevelFilters` merely happens not to emit while a link is
     * active. That is precisely the reasoning `canRemoveLayer` rejects for itself: a
     * guard that lives in a child's rendering is one prop-drilling mistake, or one
     * "show me what my own filters would add" affordance, away from being gone.
     * Refusing at the primitive makes it structural instead of incidental, for every
     * caller, and costs nothing today because the button is already unclickable.
     */
    if (linkActive) return;
    const clearPatch: BaseViewConfigPatch = {
      levelFilters: Object.fromEntries(levels.map((level) => [level.id, null])),
    };
    patchSessionBase(clearPatch);
    personalization.patchBase(clearPatch);
  }, [levels, linkActive, patchSessionBase, personalization]);

  const pendingSharedFilterLevelIds = useMemo(
    () => Object.keys(pendingSharedFilters),
    [pendingSharedFilters]
  );

  const canRemoveLayer = useCallback(
    (layer: FilterLayer): boolean => {
      if (layer === 'context') return false;
      // A link's clauses belong to the sender, not the recipient. They are also
      // not a save target — `setLevelFilter`'s final branch treats any non
      // session/user target as the VIEW buffer, so a `link` removal would write
      // the sender's clauses into the recipient's shared view: exactly what
      // `deriveViewSaveReason`'s `link_active` exists to prevent.
      if (layer === 'link') return false;
      if (layer === 'view') return !deriveViewSaveReason();
      return true;
    },
    [deriveViewSaveReason]
  );

  const removeFilterClause = useCallback(
    (levelId: string, layer: FilterLayer, lane: FilterProvenanceEntry['lane'], filterId: string) => {
      // Neither layer is the caller's to remove, and neither is a save target.
      // Narrowing both out here leaves `view`/`user`/`session`, which ARE
      // exactly `FilterSaveTarget` — so the call below needs no cast, and a
      // future layer added to `FilterLayer` becomes a compile error rather
      // than a silent write into the shared-view buffer.
      if (layer === 'context' || layer === 'link') return;
      const composed = byLevel[levelId];
      if (!composed) return;

      // The other `groupComposedFiltersByLayer` consumer on a write path, and
      // the one place besides the popover where suppression could produce a
      // replace-with-`{}` (EAINT-793 R-1). It cannot: while a link is active
      // `provenance` carries only `context`/`link`, so `laneProvenance` is
      // empty, `index` is -1, and this returns before reaching `setLevelFilter`.
      const layerFilters = groupComposedFiltersByLayer(composed)[layer];
      const laneEntries = (layerFilters[lane] as unknown as Array<{ id?: string }> | undefined) ?? [];
      // Index-aligned with `laneEntries` by the same invariant
      // `groupComposedFiltersByLayer` documents: `provenance` filtered to
      // this (layer, lane) lists entries in the same relative order.
      const laneProvenance = composed.provenance.filter((p) => p.layer === layer && p.lane === lane);
      const index = laneProvenance.findIndex((p) => p.filterId === filterId);
      if (index === -1) return;

      const nextLaneEntries = laneEntries.filter((_, i) => i !== index);
      setLevelFilter(levelId, { ...layerFilters, [lane]: nextLaneEntries }, layer);
    },
    [byLevel, setLevelFilter]
  );

  return {
    byLevel,
    runtimeFilters,
    globalContextTiers,
    saveTargets,
    setLevelFilter,
    hasPendingSharedFilters,
    pendingSharedFilterLevelIds,
    commitPendingSharedFilters,
    discardPendingSharedFilters,
    reset,
    degraded,
    canRemoveLayer,
    removeFilterClause,
    personalization,
  };
}

export default useLevelFilters;
